<template>
    <div class="field-container-2">
        <div class="regular-type-header">
            {{header}}
        </div>
        <template v-if="field.allowsMultiple">
            <div v-for="(each, index) in value" v-bind:key="index">
                <div class="deletable-text-field-container">
                    <v-text-field
                            type="text"
                            :persistent-hint="true"
                            color="accent"
                            v-model="value[index]"
                            :rules="validationRules"
                            :required="isRequired"
                            @input="emitInput"
                            v-on:update:error="emitValidationState"
                    ></v-text-field>
                    <div v-if="value.length > 1" class="delete-entry" v-on:click="removeInputField(index)">
                        <font-awesome-icon :icon="['fal', 'minus-circle']"/>
                    </div>
                </div>
            </div>
            <div class="add-input-container">
                <div v-if="validationText !== ''" class="hint-text custom-validation-error-text">
                    {{validationText}}
                </div>
                <div v-else class="hint-text">{{description}}</div>
                <v-btn small fab :color="'success'" v-on:click="addInputField"
                       :disabled="value[value.length - 1] === ''">
                    <span><font-awesome-icon :icon="['fal', 'plus']"/></span>
                </v-btn>
            </div>
        </template>
        <template v-else>
            <v-select v-if="isEnumMode && enums" :hint="description" color="accent" persistent-hint
                      :items="enums" v-model="value" @input="emitInput"></v-select>
            <v-text-field v-else
                          type="text"
                          :hint="description"
                          :persistent-hint="true"
                          color="accent"
                          v-model="value"
                          :rules="validationRules"
                          :required="isRequired"
                          @input="emitInput"
                          v-on:update:error="emitValidationState"
            ></v-text-field>
        </template>

    </div>

</template>

<script>
    import {
        EVENT_INPUT,
        EVENT_TOUCHED,
        EVENT_VALIDATION_STATE,
        DEFAULT_LOCALE,
        GET_LOCALE_DISPLAY_NAME,
        VALIDATION_ENUM,
    } from '../../services/constants'
    import helpers from '../../services/helpers'

    export default {
        name: 'sfr-text-short-field',
        props: {
            field: Object,
            valid: Boolean,
            inputValue: [String, Array],
        },
        model: {
            prop: 'inputValue',
            event: EVENT_INPUT
        },
        data: function () {
            return {
                isRequired: this.field.required,
                value: this.inputValue,
                validationText: '',
            }
        },
        computed: {
            header: function () {
                return this.computeFieldHeader(this.field)
            },
            isValid: function () {
                return this.valid
            },
            validationRules: function () {
                return [
                    this.isRequired && !this.field.allowsMultiple ? v => !!v || `${this.field.name} is required` : () => true,
                    v => v.length <= 256 || `${this.field.name} must be less than 256 characters`
                ]
            },
            description: function () {
                const currentLocale = this.$store.state.locale.currentLocale
                return currentLocale === DEFAULT_LOCALE ? this.field.description : `${this.field.description} (${this.$store.getters[GET_LOCALE_DISPLAY_NAME](currentLocale)})`
            },
            isEditMode: function () {
                return helpers.isEditRoute(this.$route)
            },
            isEnumMode() {
                return !!this.field.validations.find(validation => {
                    return validation.name === VALIDATION_ENUM
                })
            },
            enums() {
                return this.isEnumMode ? this.field.validations.find(validation => {
                    return validation.name === VALIDATION_ENUM
                }).value.split(',') : null
            }
        },
        watch: {
            inputValue: function (newValue, oldValue) {
                this.value = newValue
                this.validate()
            },
        },
        methods: {
            computeFieldHeader: function (field) {
                return `${field.name}${field.required ? '* ' : ''}:`
            },
            emitInput: function () {
                this.$emit(EVENT_INPUT, this.value)
                this.$emit(EVENT_TOUCHED, true)
            },
            emitValidationState: function ($event) {
                this.$emit(EVENT_VALIDATION_STATE, !$event)
            },
            validate: function () {
                if (this.field.allowsMultiple) {
                    let validationResult = !this.value.map(eachStr => {
                        return !this.validationRules.map(ruleFunc => {
                            const ruleResult = ruleFunc(eachStr)
                            return ruleResult === true
                        }).some(validationResult => {
                            return validationResult === false
                        })
                    }).some(validationResult => {
                        return validationResult === false
                    })

                    if (this.isRequired) {
                        if (this.value[0] === '') {
                            validationResult = false
                            this.validationText = `${this.field.name} is required`
                        } else {
                            this.validationText = ''
                        }
                    }
                    if (validationResult !== this.isValid) {
                        this.emitValidationState(!validationResult)
                    }
                }
                else {
                    const validationResult = !this.validationRules.map(ruleFunc => {
                        const ruleResult = ruleFunc(this.value)
                        return ruleResult === true
                    }).some(validationResult => {
                        return validationResult === false
                    })
                    if (validationResult !== this.isValid) {
                        this.emitValidationState(!validationResult)
                    }
                }
            },
            removeInputField: function (index) {
                this.value.splice(index, 1)
                this.emitInput()
                this.validate()
            },
            addInputField: function () {
                this.value.push('')
            },
        },
        created: function () {
            if (this.isEditMode || !this.isRequired) {
                this.validate()
            }
        },
    }
</script>

<style scoped lang="scss">
    @import "../../meta";

    .field-container-1 {
        margin: 2em 0;
        .field-container-2 {
            padding: 0 1em;
            $border-width: 3px;
            border-left: $border-width solid $accent;
            border-radius: $border-width;
        }
    }

    .regular-type-header {
        font-size: 14px;
        .input-group {
            padding: 0px;
        }
    }

    .deletable-text-field-container {
        display: flex;
        align-items: center;
        .delete-entry {
            cursor: pointer;
            color: $error;
            margin: 0 0 0 .5em;
            font-size: 16px;
        }

    }

    .add-input-container {
        display: flex;
        justify-content: space-between;
        align-items: flex-end;
    }

    .hint-text {
        @extend %manual-description;

        &.custom-validation-error-text {
            color: $error
        }
    }


</style>
